import { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Col,
  Row,
  Form,
  InputGroup,
  Alert,
  Spinner,
  Modal
} from 'react-bootstrap';
import {
  faPhone,
  faMapPin,
  faBriefcase,
  faCertificate,
  faFileAlt,
  faEdit,
  faSave,
  faTimes,
  faDownload,
  faEye,
  faIdCard,
  faKey
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import AvatarUpload from 'components/common/AvatarUpload';
import LocationAutocomplete from 'components/common/LocationAutocomplete';
import { Coordinates } from 'features/location/googleLocation';
import classNames from 'classnames';
import team15 from 'assets/img/team/avatar-rounded.webp';
import {
  getProfile,
  updateProfile,
  downloadDocument,
  viewDocument,
  uploadProfileDocument,
  deleteProfileDocument
} from 'features/profile/profileApi';
import { forgotPassword } from 'features/auth/authApi';
import { useAuth } from 'providers/AuthProvider';
import { getAuthSession } from 'features/auth/authSession';

const normalizeUkPhoneLocal = (value: string) => {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('44')) {
    return digits.slice(2);
  }
  if (digits.startsWith('0')) {
    return digits.slice(1);
  }
  return digits;
};


const formatUkPhone = (value: string) => {
  const digits = normalizeUkPhoneLocal(value);
  return digits ? `+44${digits}` : '';
};

const DOCUMENT_TYPE_OPTIONS = [
  { value: 'passport', label: 'Passport', limit: 1 },
  { value: 'visa_work_permit', label: 'Visa/Work permit', limit: 1 },
  { value: 'safeguarding_level_2', label: 'Safeguarding level 2', limit: 1 },
  { value: 'qualification_certificates', label: 'Qualification Certificates', limit: 4 },
  { value: 'dbs_check', label: 'DBS Check', limit: 1 },
  { value: 'additional_document', label: 'Additional document', limit: 1 }
] as const;

const DOCUMENT_UPLOAD_LIMITS = DOCUMENT_TYPE_OPTIONS.reduce(
  (acc, option) => {
    acc[option.value] = option.limit;
    return acc;
  },
  {} as Record<(typeof DOCUMENT_TYPE_OPTIONS)[number]['value'], number>
);

const formatDisplayDate = (value?: string | null) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const toInputDate = (value?: string | null) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const LocumProfilePage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [profileData, setProfileData] = useState<any>(null);
  const [formData, setFormData] = useState<any>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [showDocViewer, setShowDocViewer] = useState(false);
  const [documentType, setDocumentType] = useState('additional_document');
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [isDocumentUploading, setIsDocumentUploading] = useState(false);
  const [deletingDocumentId, setDeletingDocumentId] = useState<number | null>(null);
  const [selectedCoordinates, setSelectedCoordinates] =
    useState<Coordinates | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [showDocumentRequirementsNotice, setShowDocumentRequirementsNotice] = useState(false);
  const [isPasswordResetSending, setIsPasswordResetSending] = useState(false);

  const { login } = useAuth();

  const documentCounts = useMemo(() => {
    return (profileData?.documents || []).reduce(
      (acc: Record<string, number>, doc: any) => {
        acc[doc.doc_type] = (acc[doc.doc_type] || 0) + 1;
        return acc;
      },
      {}
    );
  }, [profileData]);

  const isPharmacistRole =
    String(formData?.locum_role || '').toLowerCase() === 'pharmacist';

  const requiredDocumentTypes = useMemo(
    () =>
      DOCUMENT_TYPE_OPTIONS.filter(option => {
        if (option.value === 'passport' || option.value === 'visa_work_permit') {
          return false;
        }

        if (option.value === 'additional_document') {
          return false;
        }

        if (option.value === 'dbs_check') {
          return isPharmacistRole;
        }

        if (option.value === 'safeguarding_level_2') {
          return isPharmacistRole;
        }

        return true;
      }),
    [isPharmacistRole]
  );

  const missingRequiredDocumentTypes = useMemo(
    () =>
      requiredDocumentTypes.filter(
        option => (documentCounts[option.value] || 0) < 1
      ),
    [documentCounts, requiredDocumentTypes]
  );

  const hasAllRequiredDocuments = missingRequiredDocumentTypes.length === 0;

  useEffect(() => {
    if (hasAllRequiredDocuments) {
      setShowDocumentRequirementsNotice(false);
    }
  }, [hasAllRequiredDocuments]);

  const selectedDocumentLimit = DOCUMENT_UPLOAD_LIMITS[documentType as keyof typeof DOCUMENT_UPLOAD_LIMITS] ?? 1;
  const selectedDocumentCount = documentCounts[documentType] || 0;
  const isSelectedDocumentLimitReached = selectedDocumentCount >= selectedDocumentLimit;

  // Required fields for locum profile - match registration mandatory fields
  const requiredFields = [
    'name',
    'last_name',
    'phone',
    'qualifications',
    'experience_years',
    'national_insurance_number',
    'location',
    'address',
    'city',
    'zip_code',
    'dob',
    'gender',
    'qualification_date',
    'ref_Name_1',
    'ref_PhoneNumber_1',
    'ref_Details_1',
    'ref_Name_2',
    'ref_PhoneNumber_2',
    'ref_Details_2'
  ];

  const buildLocumFormData = (data: any) => ({
    ...data.profile,
    name: data.user.name,
    last_name: data.user.last_name,
    ref_Name_1: data.user.ref_Name_1 ?? data.profile.ref_Name_1 ?? '',
    ref_PhoneNumber_1:
      data.user.ref_PhoneNumber_1 ?? data.profile.ref_PhoneNumber_1 ?? '',
    ref_Details_1: data.user.ref_Details_1 ?? data.profile.ref_Details_1 ?? '',
    ref_Name_2: data.user.ref_Name_2 ?? data.profile.ref_Name_2 ?? '',
    ref_PhoneNumber_2:
      data.user.ref_PhoneNumber_2 ?? data.profile.ref_PhoneNumber_2 ?? '',
    ref_Details_2: data.user.ref_Details_2 ?? data.profile.ref_Details_2 ?? ''
  });

  // Fetch profile data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await getProfile();
        setProfileData(data);
        setFormData(buildLocumFormData(data));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load profile');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, []);

  // Handle form input change
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]:
        name.includes('years') || name.includes('distance')
          ? parseFloat(value)
          : value
    });
    // Clear error for this field when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors({
        ...fieldErrors,
        [name]: ''
      });
    }
  };

  // Validate required fields
  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};
    const fieldLabelOverrides: Record<string, string> = {
      zip_code: 'Postcode',
      national_insurance_number: 'National Insurance Number',
      ref_Name_1: 'Reference 1 Name',
      ref_PhoneNumber_1: 'Reference 1 Phone',
      ref_Details_1: 'Reference 1 Details',
      ref_Name_2: 'Reference 2 Name',
      ref_PhoneNumber_2: 'Reference 2 Phone',
      ref_Details_2: 'Reference 2 Details'
    };

    requiredFields.forEach(field => {
      const value = formData[field];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        const fieldLabel = fieldLabelOverrides[field] || field
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        newErrors[field] = `${fieldLabel} is required`;
      }
    });

    // Independent Prescriber is required only for Pharmacist
    if (isPharmacistRole) {
      const value = formData.independent_prescriber;
      if (value === null || value === undefined || value === '') {
        newErrors.independent_prescriber = 'Independent Prescriber is required';
      }

      const gphcNumber = formData.gphc_number;
      if (!gphcNumber || (typeof gphcNumber === 'string' && gphcNumber.trim() === '')) {
        newErrors.gphc_number = 'GPhC Number is required';
      }
    }

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    if (!hasAllRequiredDocuments) {
      setShowDocumentRequirementsNotice(true);
      setError(null);
      return;
    }

    // Validate form before saving
    if (!validateForm()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      // Include coordinates in form data if selected
      const dataToSave = { ...formData };
      if (dataToSave.phone) {
        dataToSave.phone = formatUkPhone(dataToSave.phone);
      }
      if (dataToSave.ref_PhoneNumber_1 !== undefined) {
        dataToSave.ref_PhoneNumber_1 = formatUkPhone(
          dataToSave.ref_PhoneNumber_1
        );
      }
      if (dataToSave.ref_PhoneNumber_2 !== undefined) {
        dataToSave.ref_PhoneNumber_2 = formatUkPhone(
          dataToSave.ref_PhoneNumber_2
        );
      }
      if (selectedCoordinates) {
        dataToSave.latitude = selectedCoordinates.lat;
        dataToSave.longitude = selectedCoordinates.lng;
      }

      const result = await updateProfile(dataToSave);

      // Get the current session and update it with new user data
      const currentSession = getAuthSession();
      if (currentSession) {
        login({
          token: currentSession.token,
          user: {
            ...currentSession.user,
            name: result.user.name,
            last_name: result.user.last_name
          }
        });
      }

      setSuccess(result.message || 'Profile updated successfully');
      setIsEditing(false);
      setProfileData(result);
      setFormData(buildLocumFormData(result));
      setSelectedCoordinates(null);
      setFieldErrors({});
      setShowDocumentRequirementsNotice(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setFormData(buildLocumFormData(profileData));

    setIsEditing(false);
    setError(null);
    setSelectedCoordinates(null);
    setFieldErrors({});
    setShowDocumentRequirementsNotice(false);
  };

  // Handle document download
  const handleDocumentDownload = async (document: any) => {
    try {
      console.log('Starting download for:', document.document_name);
      await downloadDocument(document.document_name);
      console.log('Download successful for:', document.document_name);
      setSuccess('Document downloaded successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : 'Failed to download document';
      console.error('Download error:', errorMsg);
      setError(errorMsg);
    }
  };

  // Handle document view
  const handleDocumentView = (document: any) => {
    viewDocument(document.document_name);
  };

  const handleChangePassword = async () => {
    const email = profileData?.user?.email;

    if (!email) {
      setError('Unable to find the account email address');
      return;
    }

    try {
      setIsPasswordResetSending(true);
      setError(null);
      setSuccess(null);

      await forgotPassword({ email });

      setSuccess('A password reset link has been sent to your email address.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email');
    } finally {
      setIsPasswordResetSending(false);
    }
  };

  const handleDocumentUpload = async () => {
    if (!documentFile) {
      setError('Please choose a document file to upload');
      return;
    }

    if (isSelectedDocumentLimitReached) {
      setError(
        selectedDocumentLimit === 1
          ? 'You can only upload one document of this type'
          : `You can upload up to ${selectedDocumentLimit} documents of this type`
      );
      return;
    }

    try {
      setIsDocumentUploading(true);
      setError(null);

      const updatedProfile = await uploadProfileDocument({
        docType: documentType,
        file: documentFile
      });

      setProfileData(updatedProfile);
      setFormData(buildLocumFormData(updatedProfile));
      setDocumentFile(null);
      const nextAvailableType = DOCUMENT_TYPE_OPTIONS.find(
        option => (documentCounts[option.value] || 0) < option.limit
      );
      setDocumentType(nextAvailableType?.value || 'additional_document');
      setSuccess('Document uploaded successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload document');
    } finally {
      setIsDocumentUploading(false);
    }
  };

  const handleDocumentDelete = async (document: any) => {
    if (!window.confirm('Delete this document?')) {
      return;
    }

    try {
      setDeletingDocumentId(document.id);
      setError(null);

      const updatedProfile = await deleteProfileDocument(document.id);

      setProfileData(updatedProfile);
      setFormData(buildLocumFormData(updatedProfile));
      setSuccess('Document deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete document');
    } finally {
      setDeletingDocumentId(null);
    }
  };

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '400px' }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (!profileData) {
    return <Alert variant="danger">Failed to load profile</Alert>;
  }

  return (
    <div>
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
          {success}
        </Alert>
      )}
      {profileData?.profile?.approval_status === "REJECTED" && (
        <div className="mb-4 p-3 border rounded bg-body-tertiary">
          <h6 className="mb-2 text-body mb-0">
            Profile Requires Changes
          </h6>

          <p className="mb-2 text-body-secondary">
            Your profile could not be approved during our compliance review.
          </p>

          <p className="mb-2 text-body-secondary">
            Reason: {profileData.profile.approval_reason}
          </p>

          <p className="mb-0 text-body-secondary">
            Please update your profile and save your changes.
            Once submitted, your profile will automatically be sent for review again.
          </p>
        </div>
      )}
      {profileData?.profile?.approval_status === "PENDING" && (
        <div className="mb-4 p-3 border rounded bg-body-tertiary">
          <h6 className="mb-2 text-body">Profile Under Review</h6>
          <p className="mb-0 text-body-secondary">
            {profileData.message ||
              'Your profile is currently under review. We will notify you once the compliance review is complete.'}
          </p>
        </div>
      )}

      {/* Profile Header Card */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="align-items-center g-4">
            <Col xs={12} sm="auto">
              <AvatarUpload size="5xl" src={team15} />
            </Col>
            <Col xs={12} sm>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  {isEditing ? (
                    <div className="mb-3">
                      <Row className="g-2">
                        <Col md={6}>
                          <Form.Group className="text-start mb-2">
                            <Form.Label>
                              First Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="name"
                              value={formData.name || ''}
                              onChange={handleInputChange}
                              placeholder="First Name"
                              isInvalid={!!fieldErrors.name}
                            />
                            {fieldErrors.name && (
                              <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                                {fieldErrors.name}
                              </Form.Control.Feedback>
                            )}
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="text-start mb-2">
                            <Form.Label>
                              Last Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="last_name"
                              value={formData.last_name || ''}
                              onChange={handleInputChange}
                              placeholder="Last Name"
                              isInvalid={!!fieldErrors.last_name}
                            />
                            {fieldErrors.last_name && (
                              <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                                {fieldErrors.last_name}
                              </Form.Control.Feedback>
                            )}
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>
                  ) : (
                    <h2 className="mb-1">{formData.name} {formData.last_name}</h2>
                  )}
                  <p className="text-body-secondary mb-2">
                    <FontAwesomeIcon icon={faPhone} className="me-2" />
                    {isEditing ? (
                      <InputGroup className="d-inline-flex" style={{ width: '240px' }}>
                        <InputGroup.Text>+44</InputGroup.Text>
                        <Form.Control
                          type="tel"
                          name="phone"
                          value={normalizeUkPhoneLocal(formData.phone || '')}
                          onChange={e => {
                            const newValue = normalizeUkPhoneLocal(e.target.value);
                            setFormData({
                              ...formData,
                              phone: newValue
                            });
                            if (fieldErrors.phone) {
                              setFieldErrors({
                                ...fieldErrors,
                                phone: ''
                              });
                            }
                          }}
                          inputMode="numeric"
                          pattern="\\d{9,10}"
                          title="Enter 9-10 digits after +44"
                          isInvalid={!!fieldErrors.phone}
                        />
                      </InputGroup>
                    ) : (
                      profileData?.profile?.phone || 'N/A'
                    )}
                  </p>
                  <p className="text-body-secondary mb-0">
                    <FontAwesomeIcon icon={faMapPin} className="me-2" />
                    {profileData?.profile?.location || 'Not set'}
                  </p>
                </div>
                <div>
                  <div className="d-flex flex-column gap-2 align-items-stretch">
                    {!isEditing ? (
                      <Button
                        variant="phoenix-primary"
                        onClick={() => setIsEditing(true)}
                        startIcon={<FontAwesomeIcon icon={faEdit} />}
                        size="sm"
                      >
                        Edit Profile
                      </Button>
                    ) : (
                      <div className="d-flex gap-2">
                        <Button
                          variant="success"
                          onClick={handleSave}
                          loading={isSaving}
                          startIcon={<FontAwesomeIcon icon={faSave} />}
                          size="sm"
                        >
                          Save
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={handleCancel}
                          startIcon={<FontAwesomeIcon icon={faTimes} />}
                          size="sm"
                        >
                          Cancel
                        </Button>
                      </div>
                    )}

                    <Button
                      variant="outline-primary"
                      onClick={handleChangePassword}
                      loading={isPasswordResetSending}
                      startIcon={<FontAwesomeIcon icon={faKey} />}
                      size="sm"
                    >
                      Change Password
                    </Button>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
        {showDocumentRequirementsNotice && (
          <div className="m-4 rounded border border-translucent bg-body-tertiary p-5">
            <div className="d-flex flex-column gap-2">
              <div className="d-flex align-items-center justify-content-between gap-3">
                <span className="fw-semibold text-body">Document requirements</span>
                <span className="text-body-secondary small">
                  {hasAllRequiredDocuments ? 'All set' : 'Action needed'}
                </span>
              </div>

              <p className="text-body-secondary small mb-0">
                {hasAllRequiredDocuments
                  ? 'All required document types are uploaded.'
                  : `Please upload at least one of these document types before saving: ${missingRequiredDocumentTypes.map(option => option.label).join(', ')}.`}
              </p>
            </div>
          </div>
        )}

      </Card>

      {/* Professional Details Card */}
      <Card className="mb-4">
        <Card.Header className="bg-light">
          <Card.Title className="mb-0">
            <FontAwesomeIcon icon={faBriefcase} className="me-2 text-primary" />
            Professional Details
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group className="text-start">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={profileData?.user?.email || ''}
                  disabled
                  placeholder="Email"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="text-start">
                <Form.Label>
                  Address <span className="text-danger">*</span>
                </Form.Label>
                {isEditing ? (
                  <Form.Control
                    type="text"
                    name="address"
                    value={formData.address || ''}
                    onChange={handleInputChange}
                    placeholder="Street address"
                    required
                    isInvalid={!!fieldErrors.address}
                  />
                ) : (
                  <Form.Control
                    type="text"
                    value={formData.address || ''}
                    disabled
                  />
                )}
                {fieldErrors.address && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.address}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="text-start">
                <Form.Label>
                  Qualifications <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="qualifications"
                  value={formData.qualifications || ''}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Qualifications"
                  isInvalid={!!fieldErrors.qualifications}
                />
                {fieldErrors.qualifications && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.qualifications}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="text-start">
                <Form.Label>
                  Years of Experience <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  name="experience_years"
                  value={formData.experience_years || ''}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Years of Experience"
                  className="input-spin-none"
                  isInvalid={!!fieldErrors.experience_years}
                />
                {fieldErrors.experience_years && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.experience_years}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="text-start">
                <Form.Label>Locum Role</Form.Label>
                {isEditing ? (
                  <Form.Select
                    name="locum_role"
                    value={formData?.locum_role || 'pharmacist'}
                    onChange={handleInputChange}
                  >
                    <option value="pharmacist">Pharmacist</option>
                    <option value="technician">Technician</option>
                    <option value="dispenser">Dispenser</option>
                  </Form.Select>
                ) : (
                  <Form.Control
                    type="text"
                    value={
                      formData?.locum_role === 'pharmacist'
                        ? 'Pharmacist'
                        : formData?.locum_role === 'technician'
                          ? 'Technician'
                          : 'Dispenser'
                    }
                    disabled
                  />
                )}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="text-start">
                <Form.Label>
                  GPhC Number {isPharmacistRole && <span className="text-danger">*</span>}
                </Form.Label>
                <Form.Control
                  type="text"
                  name="gphc_number"
                  value={formData.gphc_number || ''}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="GPhC Number"
                  required={isPharmacistRole}
                  isInvalid={!!fieldErrors.gphc_number}
                />
                {fieldErrors.gphc_number && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.gphc_number}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="text-start">
                <Form.Label>
                  National Insurance Number <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="national_insurance_number"
                  value={formData.national_insurance_number || ''}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="National Insurance Number"
                  required
                  isInvalid={!!fieldErrors.national_insurance_number}
                />
                {fieldErrors.national_insurance_number && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.national_insurance_number}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="text-start">
                <Form.Label>
                  City <span className="text-danger">*</span>
                </Form.Label>
                {isEditing ? (
                  <Form.Control
                    type="text"
                    name="city"
                    value={formData.city || ''}
                    onChange={handleInputChange}
                    placeholder="City"
                    required
                    isInvalid={!!fieldErrors.city}
                  />
                ) : (
                  <Form.Control type="text" value={formData.city || ''} disabled />
                )}
                {fieldErrors.city && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.city}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="text-start">
                <Form.Label>
                  Postcode <span className="text-danger">*</span>
                </Form.Label>
                {isEditing ? (
                  <Form.Control
                    type="text"
                    name="zip_code"
                    value={formData.zip_code || ''}
                    onChange={handleInputChange}
                    placeholder="Postcode"
                    required
                    isInvalid={!!fieldErrors.zip_code}
                  />
                ) : (
                  <Form.Control type="text" value={formData.zip_code || ''} disabled />
                )}
                {fieldErrors.zip_code && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.zip_code}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="text-start">
                <Form.Label>
                  Date of Birth <span className="text-danger">*</span>
                </Form.Label>
                {isEditing ? (
                  <Form.Control
                    type="date"
                    name="dob"
                    value={toInputDate(formData.dob) || ''}
                    onChange={handleInputChange}
                    placeholder="Date of Birth"
                    isInvalid={!!fieldErrors.dob}
                  />
                ) : (
                  <Form.Control type="text" value={formatDisplayDate(formData.dob)} disabled />
                )}
                {fieldErrors.dob && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.dob}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="text-start">
                <Form.Label>
                  Gender <span className="text-danger">*</span>
                </Form.Label>
                {isEditing ? (
                  <Form.Select
                    name="gender"
                    value={formData.gender || ''}
                    onChange={handleInputChange}
                    isInvalid={!!fieldErrors.gender}
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </Form.Select>
                ) : (
                  <Form.Control type="text" value={formData.gender || ''} disabled />
                )}
                {fieldErrors.gender && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.gender}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="text-start">
                <Form.Label>
                  Qualification Date <span className="text-danger">*</span>
                </Form.Label>
                {isEditing ? (
                  <Form.Control
                    type="date"
                    name="qualification_date"
                    value={toInputDate(formData.qualification_date) || ''}
                    onChange={handleInputChange}
                    isInvalid={!!fieldErrors.qualification_date}
                  />
                ) : (
                  <Form.Control type="text" value={formatDisplayDate(formData.qualification_date)} disabled />
                )}
                {fieldErrors.qualification_date && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.qualification_date}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="text-start">
                <Form.Label>
                  Independent Prescriber <span className="text-danger">*</span>
                </Form.Label>
                {isEditing ? (
                  <>
                    <div
                      className="d-flex gap-3 align-items-center pt-2"
                      style={{ minHeight: '38px' }}
                    >
                      <Form.Check
                        type="radio"
                        id="independent-prescriber-yes-edit"
                        name="independent_prescriber"
                        value="1"
                        label="Yes"
                        checked={String(formData.independent_prescriber) === '1'}
                        onChange={handleInputChange}
                      />
                      <Form.Check
                        type="radio"
                        id="independent-prescriber-no-edit"
                        name="independent_prescriber"
                        value="0"
                        label="No"
                        checked={String(formData.independent_prescriber) === '0'}
                        onChange={handleInputChange}
                      />
                    </div>
                    {fieldErrors.independent_prescriber && (
                      <div style={{ color: '#dc3545', fontSize: '0.875em', marginTop: '0.25rem' }}>
                        {fieldErrors.independent_prescriber}
                      </div>
                    )}
                  </>
                ) : (
                  <Form.Control
                    type="text"
                    value={formData.independent_prescriber === 1 || formData.independent_prescriber === '1' ? 'Yes' : formData.independent_prescriber === 0 || formData.independent_prescriber === '0' ? 'No' : ''}
                    disabled
                  />
                )}
              </Form.Group>
            </Col>
            {/* <Col md={6}>
  <div className="form-icon-container">
    <FloatingLabel controlId="locum_role" label="Locum Role">
      <Form.Control
        type="text"
        value={
          formData?.locum_role === 'pharmacist'
            ? 'Pharmacist'
            : 'Technician'
        }
        disabled // ✅ always disabled
       
      />
    </FloatingLabel>
  </div>
</Col> */}
          </Row>
        </Card.Body>
      </Card>

      {/* Location Card */}
      <Card className="mb-4">
        <Card.Header className="bg-light">
          <Card.Title className="mb-0">
            <FontAwesomeIcon icon={faMapPin} className="me-2 text-primary" />
            Service Location
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              {isEditing ? (
                <div>

                  <LocationAutocomplete
                    value={formData.location || ''}
                    onChange={location => {
                      setFormData({ ...formData, location });
                      // Clear error when user selects location
                      if (fieldErrors.location) {
                        setFieldErrors({
                          ...fieldErrors,
                          location: ''
                        });
                      }
                    }}
                    onCoordinatesChange={setSelectedCoordinates}
                    label="Address"
                    placeholder="Search location..."
                    required={true}
                  />
                </div>
              ) : (
                <Form.Group className="text-start">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    type="text"
                    name="location"
                    value={formData.location || ''}
                    disabled
                    placeholder="Location"
                  />
                </Form.Group>
              )}
              {isEditing && fieldErrors.location && (
                <div style={{ color: '#dc3545', fontSize: '0.875em', marginTop: '0.25rem' }}>
                  {fieldErrors.location}
                </div>
              )}
            </Col>
            <Col md={6}>
              <Form.Group className="text-start">
                <Form.Label>Travel Distance (miles)</Form.Label>
                <Form.Control
                  type="number"
                  name="travel_distance"
                  value={formData.travel_distance || ''}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Travel Distance"
                  className="input-spin-none"
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>


      {/* Professional References Card */}
      <Card className="mb-4">
        <Card.Header className="bg-light">
          <Card.Title className="mb-0">
            <FontAwesomeIcon icon={faIdCard} className="me-2 text-primary" />
            Professional References
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <h6 className="text-body-secondary mb-3">Reference 1</h6>
              <Form.Group className="text-start mb-3">
                <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  name="ref_Name_1"
                  value={formData.ref_Name_1 || profileData?.user?.ref_Name_1 || ''}
                  onChange={isEditing ? handleInputChange : undefined}
                  disabled={!isEditing}
                  placeholder="Name"
                  required
                  isInvalid={!!fieldErrors.ref_Name_1}
                />
                {fieldErrors.ref_Name_1 && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.ref_Name_1}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
              <Form.Group className="text-start mb-3">
                <Form.Label>Phone <span className="text-danger">*</span></Form.Label>
                <InputGroup>
                  <InputGroup.Text>+44</InputGroup.Text>
                  <Form.Control
                    type="tel"
                    name="ref_PhoneNumber_1"
                    value={normalizeUkPhoneLocal(
                      formData.ref_PhoneNumber_1 ||
                      profileData?.user?.ref_PhoneNumber_1 ||
                      ''
                    )}
                    onChange={
                      isEditing
                        ? e =>
                          setFormData({
                            ...formData,
                            ref_PhoneNumber_1: normalizeUkPhoneLocal(
                              e.target.value
                            )
                          })
                        : undefined
                    }
                    disabled={!isEditing}
                    placeholder="7XXXXXXXXX"
                    inputMode="numeric"
                    pattern="\\d{9,10}"
                    title="Enter 9-10 digits after +44"
                    required
                    isInvalid={!!fieldErrors.ref_PhoneNumber_1}
                  />
                </InputGroup>
                {fieldErrors.ref_PhoneNumber_1 && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.ref_PhoneNumber_1}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
              <Form.Group className="text-start">
                <Form.Label>Details <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  as="textarea"
                  name="ref_Details_1"
                  value={formData.ref_Details_1 || profileData?.user?.ref_Details_1 || ''}
                  onChange={isEditing ? handleInputChange : undefined}
                  disabled={!isEditing}
                  placeholder="Details"
                  style={{ minHeight: '80px' }}
                  required
                  isInvalid={!!fieldErrors.ref_Details_1}
                />
                {fieldErrors.ref_Details_1 && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.ref_Details_1}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <h6 className="text-body-secondary mb-3">Reference 2</h6>
              <Form.Group className="text-start mb-3">
                <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  name="ref_Name_2"
                  value={formData.ref_Name_2 || profileData?.user?.ref_Name_2 || ''}
                  onChange={isEditing ? handleInputChange : undefined}
                  disabled={!isEditing}
                  placeholder="Name"
                  required
                  isInvalid={!!fieldErrors.ref_Name_2}
                />
                {fieldErrors.ref_Name_2 && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.ref_Name_2}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
              <Form.Group className="text-start mb-3">
                <Form.Label>Phone <span className="text-danger">*</span></Form.Label>
                <InputGroup>
                  <InputGroup.Text>+44</InputGroup.Text>
                  <Form.Control
                    type="tel"
                    name="ref_PhoneNumber_2"
                    value={normalizeUkPhoneLocal(
                      formData.ref_PhoneNumber_2 ||
                      profileData?.user?.ref_PhoneNumber_2 ||
                      ''
                    )}
                    onChange={
                      isEditing
                        ? e =>
                          setFormData({
                            ...formData,
                            ref_PhoneNumber_2: normalizeUkPhoneLocal(
                              e.target.value
                            )
                          })
                        : undefined
                    }
                    disabled={!isEditing}
                    placeholder="7XXXXXXXXX"
                    inputMode="numeric"
                    pattern="\\d{9,10}"
                    title="Enter 9-10 digits after +44"
                    required
                    isInvalid={!!fieldErrors.ref_PhoneNumber_2}
                  />
                </InputGroup>
                {fieldErrors.ref_PhoneNumber_2 && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.ref_PhoneNumber_2}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
              <Form.Group className="text-start">
                <Form.Label>Details <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  as="textarea"
                  name="ref_Details_2"
                  value={formData.ref_Details_2 || profileData?.user?.ref_Details_2 || ''}
                  onChange={isEditing ? handleInputChange : undefined}
                  disabled={!isEditing}
                  placeholder="Details"
                  style={{ minHeight: '80px' }}
                  required
                  isInvalid={!!fieldErrors.ref_Details_2}
                />
                {fieldErrors.ref_Details_2 && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.ref_Details_2}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>



      {/* Documents Card */}
      <Card className='mb-5'>
        <Card.Header className="bg-light">
          <Card.Title className="mb-0">
            <FontAwesomeIcon icon={faFileAlt} className="me-2 text-primary" />
            Documents ({profileData.documents.length})
          </Card.Title>
        </Card.Header>
        <Card.Body>
          {isEditing && (
            <>
              <p className="text-body-secondary small mb-3">
                {hasAllRequiredDocuments
                  ? 'All required document types are uploaded.'
                  : 'At least one of every required document type is needed before you can save your profile.'}
              </p>


              <div className="border rounded-3 p-3 mb-4 bg-body-emphasis">
                <h6 className="mb-3 text-body-highlight">Upload new document</h6>
                <Row className="g-3 align-items-start">
                  <Col md={4} className="d-flex">
                    <Form.Group className="text-start d-flex flex-column w-100">
                      <Form.Label>Document type</Form.Label>
                      <Form.Select
                        value={documentType}
                        onChange={e => setDocumentType(e.target.value)}
                      >
                        {DOCUMENT_TYPE_OPTIONS.map(option => (
                          <option
                            key={option.value}
                            value={option.value}
                            disabled={(documentCounts[option.value] || 0) >= option.limit}
                          >
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-body-tertiary d-block mt-2" style={{ minHeight: '1.25rem' }}>
                        {selectedDocumentCount}/{selectedDocumentLimit} uploaded for this type.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={5} className="d-flex">
                    <Form.Group className="text-start d-flex flex-column w-100">
                      <Form.Label>Choose file</Form.Label>
                      <Form.Control
                        type="file"
                        accept=".pdf,.doc,.docx,image/*"
                        onChange={e =>
                          setDocumentFile((e.target as HTMLInputElement).files?.[0] ?? null)
                        }
                      />
                      {documentFile && (
                        <Form.Text className="text-body-tertiary">
                          Selected: {documentFile.name}
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={3} className="d-flex">
                    <div className="d-flex flex-column justify-content-start w-100" style={{ paddingTop: '0.95rem' }}>
                      <Button
                        variant="phoenix-primary"
                        className="w-100"
                        onClick={handleDocumentUpload}
                        loading={isDocumentUploading}
                        disabled={isDocumentUploading || !documentFile || isSelectedDocumentLimitReached}
                      >
                        Upload document
                      </Button>
                      {isSelectedDocumentLimitReached && (
                        <Form.Text className="text-danger d-block mt-2" style={{ minHeight: '1.25rem' }}>
                          This document type has reached its upload limit.
                        </Form.Text>
                      )}
                    </div>
                  </Col>
                </Row>
              </div></>)}

          {profileData.documents.length > 0 ? (
            <div className="list-group">
              {profileData.documents.map((doc: any) => (
                <div
                  key={doc.id}
                  className="list-group-item d-flex justify-content-between align-items-center"
                >
                  <div>
                    <h6 className="mb-1 d-flex align-items-center">
                      <FontAwesomeIcon
                        icon={faCertificate}
                        className="me-2 text-warning"
                      />
                      {doc.doc_type.replace(/_/g, ' ').toUpperCase()}
                    </h6>
                    <small className="text-body-secondary">
                      {doc.document_name}
                    </small>
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      variant="phoenix-secondary"
                      size="sm"
                      startIcon={<FontAwesomeIcon icon={faEye} />}
                      onClick={() => handleDocumentView(doc)}
                    >
                      View
                    </Button>
                    <Button
                      variant="phoenix-primary"
                      size="sm"
                      startIcon={<FontAwesomeIcon icon={faDownload} />}
                      onClick={() => handleDocumentDownload(doc)}
                    >
                      Download
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      startIcon={<FontAwesomeIcon icon={faTimes} />}
                      onClick={() => handleDocumentDelete(doc)}
                      loading={deletingDocumentId === doc.id}
                      disabled={deletingDocumentId === doc.id}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Alert variant="info" className="mb-0">
              No documents uploaded yet
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Document Viewer Modal */}
      <Modal
        show={showDocViewer}
        onHide={() => setShowDocViewer(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedDocument?.doc_type.replace(/_/g, ' ').toUpperCase()}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDocument && (
            <div className="text-center">
              <p className="mb-3 text-body-secondary">
                {selectedDocument.document_name}
              </p>
              <div
                style={{
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  padding: '20px',
                  backgroundColor: '#f8f9fa',
                  minHeight: '400px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column'
                }}
              >
                <FontAwesomeIcon
                  icon={faFileAlt}
                  size="3x"
                  className="text-muted mb-3"
                />
                <p className="text-muted mb-2">
                  <strong>{selectedDocument.document_name}</strong>
                </p>
                <p className="text-muted small mb-4">
                  Click the download button to view or download this document.
                </p>
                <Button
                  variant="phoenix-primary"
                  startIcon={<FontAwesomeIcon icon={faDownload} />}
                  onClick={() => {
                    handleDocumentDownload(selectedDocument);
                  }}
                >
                  Open Document
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default LocumProfilePage;
